import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class XgsGuard implements CanActivate {
  private redirectingToSSO = false;

  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean {
    // If this is an OIDC callback (has code parameter), process it first
    if (route.queryParams['code']) {
      console.log('[XgsGuard] ===== OIDC CALLBACK DETECTED =====');
      console.log('[XgsGuard] Full URL:', window.location.href);
      console.log('[XgsGuard] Query params:', route.queryParams);

      // Mark that we're processing callback to prevent re-redirect
      sessionStorage.setItem('processing_callback', 'true');

      return this.oidcSecurityService.checkAuth().pipe(
        take(1),
        tap(authResult => {
          console.log('[XgsGuard] Auth processing complete:', authResult);
          console.log(
            '[XgsGuard] Is authenticated:',
            authResult.isAuthenticated
          );
          console.log('[XgsGuard] Error message:', authResult.errorMessage);
        }),
        map(authResult => {
          // Clear the callback processing flag
          sessionStorage.removeItem('processing_callback');

          if (authResult.isAuthenticated) {
            console.log('[XgsGuard] ✅ Authentication successful!');
            // Reset counters
            this.redirectingToSSO = false;
            sessionStorage.removeItem('auth_retry_count');

            // Get saved route or use current path without query params
            const savedRoute = sessionStorage.getItem('route');
            sessionStorage.removeItem('route');

            // If there's a saved route and it's different from current, redirect there
            if (
              savedRoute &&
              savedRoute !== state.url &&
              !savedRoute.includes('?code=')
            ) {
              console.log('[XgsGuard] Redirecting to saved route:', savedRoute);
              return this.router.createUrlTree([savedRoute]);
            }

            // Otherwise stay on current page (remove query params)
            console.log(
              '[XgsGuard] Staying on current page, removing query params'
            );
            return this.router.createUrlTree([state.url.split('?')[0]]);
          } else {
            console.error(
              '[XgsGuard] ❌ Authentication failed after callback!'
            );
            console.error('[XgsGuard] Error:', authResult.errorMessage);
            console.error('[XgsGuard] Access token:', authResult.accessToken);
            console.error('[XgsGuard] ID token:', authResult.idToken);

            // Reset the redirect flag to allow retry
            this.redirectingToSSO = false;

            // Check if we've already tried multiple times
            const retryCount = parseInt(
              sessionStorage.getItem('auth_retry_count') || '0'
            );
            if (retryCount >= 3) {
              console.error(
                '[XgsGuard] Too many auth failures, stopping retries'
              );
              sessionStorage.removeItem('auth_retry_count');
              // Show error - for now just allow through to show the app with an error message
              return true;
            }

            // Increment retry counter
            sessionStorage.setItem(
              'auth_retry_count',
              (retryCount + 1).toString()
            );

            // Try SSO again after a brief delay
            console.log(
              '[XgsGuard] Will retry SSO (attempt',
              retryCount + 1,
              'of 3)'
            );
            setTimeout(() => {
              this.triggerSSO();
            }, 1000);

            return false;
          }
        })
      );
    }

    // Check if we're currently processing a callback
    const processingCallback = sessionStorage.getItem('processing_callback');
    if (processingCallback === 'true') {
      console.log(
        '[XgsGuard] Currently processing callback, allowing through...'
      );
      return true;
    }

    // Normal authentication check (not a callback)
    return this.oidcSecurityService.isAuthenticated$.pipe(
      take(1),
      tap(auth => {
        console.log('[XgsGuard] isAuthenticated$ value:', auth);
      }),
      map(({ isAuthenticated }) => {
        console.log(
          '[XgsGuard] Checking access to:',
          state.url,
          '| Authenticated:',
          isAuthenticated
        );

        if (!isAuthenticated) {
          console.log('[XgsGuard] Not authenticated, triggering SSO...');

          // Prevent multiple redirects
          if (!this.redirectingToSSO) {
            // Save the intended route
            sessionStorage.setItem('route', state.url);
            // Trigger SSO redirect
            this.triggerSSO();
          } else {
            console.log('[XgsGuard] Already redirecting to SSO, skipping...');
          }

          return false;
        }

        console.log('[XgsGuard] Access granted');
        return true;
      })
    );
  }

  private triggerSSO(): void {
    this.redirectingToSSO = true;
    console.log('[XgsGuard] Triggering OIDC authorization...');

    // Use the OIDC library's authorize method instead of manual redirect
    // This ensures proper callback handling
    this.oidcSecurityService.authorize();
  }
}
